import { type FormikProps } from "formik";
import { type FeedbackPageFormFields, type FeedbackPageFormProps } from "../types";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";
import { ValidatedForm } from "components/forms";
import { type VideoFeedbackPageTask } from "features/BackgroundTasks/taskPool";
import { TaskStatuses } from "enums";
import { validationSchemas } from "utils";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindActionCreators } from "redux";
import { bindAction } from "interfaces/redux";
import { connect, type ConnectedProps } from "react-redux";
import * as validationActions from "../../state/thunks/campaignValidationThunk";
import { setInitialized, setIsValid } from "../../state/slices/campaignValidationSlice";
import { campaignValidationSelector } from "../../state/selectors";
import { useFormikValidation } from "features/Library/Common/Hooks/useFormikValidationHook";

export type FeedbackPageFormPropsAll = FormikProps<FeedbackPageFormFields> & FeedbackPageFormProps & PropsFromRedux;

const FeedbackPageForm = (props: FeedbackPageFormPropsAll) => {
  const validationStep = "feedbackPageValidation";
  const {
    values,
    isLoading,
    onBlur,
    disabled,
    handleFileChange,
    uploadFilesHandler,
    task,
    errors,
    activeErrors,
    validateActions,
    isFormValid,
    resolved,
    touchedSnapshot,
    initialized,
    save,
  } = props;

  const { setActiveErrorsAction, checkForResolvedErrors } = validateActions;

  const getUploadingError = (videoUploadTask: VideoFeedbackPageTask) =>
    TaskStatuses.isFailed(videoUploadTask.status) ? videoUploadTask.errorLabel : null;

  const formik = extractFormikProps<FeedbackPageFormFields, FeedbackPageFormPropsAll>(props);
  const nameof = nameofFactory<FeedbackPageFormFields>();

  useFormikValidation(
    { errors, resolved, initialized, activeErrors, isFormValid },
    {
      setInitializedAction: (isInitialized) => props.setInitialized({ validationStep, isInitialized }),
      setActiveErrors: (errors) => setActiveErrorsAction(validationStep, errors),
      setIsValidAction: (isValid) => props.setIsValid({ validationStep, isValid }),
      checkForResolvedErrors: (errors) => checkForResolvedErrors(validationStep, errors),
    },
  );

  const renderFields = (): React.ReactNode => {
    return (
      <div className="feedback-page-form-container">
        <ValidatedForm.UploadFileField
          {...formik}
          id="fileUpload"
          label="Feedback Video"
          loadingButtonLabel="Loading..."
          onChangeHandler={handleFileChange}
          onChangeCallback={save}
          accept="video/*"
          propertyName={nameof("uploadedVideos")}
          value={values.uploadedVideos}
          touched={!resolved ? touchedSnapshot : formik.touched}
          disabled={disabled}
          filesValidator={validationSchemas.feedbackPage.fields.uploadedVideos}
          uploadFilesHandler={uploadFilesHandler}
          uploadingError={getUploadingError(task)}
          isUploading={task.isFeedbackPageVideoUploading}
          uploadingProgress={task.percent}
          canCancel={false}
          markAsRequired
          showPercents={false}
        />
        <ValidatedForm.TextAreaField
          {...formik}
          label="Feedback Message"
          value={values.message}
          propertyName={nameof("message")}
          placeholder="Text for your feedback message"
          touched={!resolved ? touchedSnapshot : formik.touched}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
    );
  };

  return (
    <ValidatedForm
      dirty={props.dirty}
      disablePreventTransitionPrompt
      isLoading={isLoading}
      formWidthComputer={6}
      withoutFormik
    >
      {renderFields}
    </ValidatedForm>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    ...campaignValidationSelector(state).feedbackPageValidation,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validateActions: bindActionCreators(validationActions, dispatch),
  setIsValid: bindAction(setIsValid, dispatch),
  setInitialized: bindAction(setInitialized, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(FeedbackPageForm);
export default ConnectedComponent;
